// Small DOM helpers called from many components: classes, measurements, pointer tracking for DwsDataGrid's
// column resize, reduced motion, the document language and media-query listeners.

import { publish } from "../shared/publish";

function AddClass(elementId: string, classname: string): void {
    let element = document.getElementById(elementId);
    if (!element) return;
    element.classList.add(classname);
}

function RemoveClass(elementId: string, classname: string): void {
    let element = document.getElementById(elementId);
    if (!element) return;
    element.classList.remove(classname);
}

function GetElementBoundingRect(element: Element): { left: number; top: number; width: number; height: number } {
    let rect = element.getBoundingClientRect();
    return { left: rect.left, top: rect.top, width: rect.width, height: rect.height };
}

function getVirtualScrollTop(element: Element): number {
    return element.scrollTop;
}

function getVirtualClientHeight(element: Element): number {
    return element.clientHeight;
}

function getVirtualScrollHeight(element: Element): number {
    return element.scrollHeight;
}

// Pointer (not mouse) events, so the DataGrid column resize also works by touch: a dragging finger sends no
// mousemove on iOS, and pointercancel ends the drag when the browser takes the gesture over.
let mouseMoveHandler: ((e: PointerEvent) => void) | null = null;
let mouseUpHandler: (() => void) | null = null;
let mouseMoveDotNetHelper: DotNetObject | null = null;
let mouseUpDotNetHelper: DotNetObject | null = null;

function addMouseMoveListener(dotNetHelper: DotNetObject): void {
    mouseMoveDotNetHelper = dotNetHelper;

    if (!mouseMoveHandler) {
        mouseMoveHandler = function (e) {
            if (mouseMoveDotNetHelper) {
                mouseMoveDotNetHelper.invokeMethodAsync('OnMouseMove', e.clientX);
            }
        };

        document.addEventListener('pointermove', mouseMoveHandler);
    }
}

function addMouseUpListener(dotNetHelper: DotNetObject): void {
    mouseUpDotNetHelper = dotNetHelper;

    if (!mouseUpHandler) {
        mouseUpHandler = function () {
            if (mouseUpDotNetHelper) {
                mouseUpDotNetHelper.invokeMethodAsync('OnMouseUp');
            }
        };

        document.addEventListener('pointerup', mouseUpHandler);
        document.addEventListener('pointercancel', mouseUpHandler);
    }
}

function removeMouseMoveListener(): void {
    if (mouseMoveHandler) {
        document.removeEventListener('pointermove', mouseMoveHandler);
        mouseMoveHandler = null;
    }

    mouseMoveDotNetHelper = null;
}

function removeMouseUpListener(): void {
    if (mouseUpHandler) {
        document.removeEventListener('pointerup', mouseUpHandler);
        document.removeEventListener('pointercancel', mouseUpHandler);
        mouseUpHandler = null;
    }

    mouseUpDotNetHelper = null;
}

// Whether the user asked the system for reduced motion. DwsCarousel starts its auto-play paused then.
function DwsPrefersReducedMotion(): boolean {
    return !!(window.matchMedia && window.matchMedia('(prefers-reduced-motion: reduce)').matches);
}

// The rendered width of an element, for components that need where a drag really starts (a DataGrid column that
// has not been resized yet has no width of its own on the .NET side).
function DwsGetElementWidth(element: Element | null): number {
    return element && typeof element.getBoundingClientRect === 'function' ? element.getBoundingClientRect().width : 0;
}

// Keeps <html lang> in step with the UI language, so assistive technology pronounces the text in the right language.
// With a direction ("rtl" / "ltr", IDwsLocalizationService.GetTextDirection()) <html dir> follows as well: the
// browser then mirrors text alignment, lists, tables and everything laid out with logical CSS properties. Anything
// else leaves dir as it is.
function DwsSetDocumentLanguage(language: string | null, direction?: string | null): void {
    if (language) {
        document.documentElement.lang = language;
    }

    if (direction === 'rtl' || direction === 'ltr') {
        document.documentElement.dir = direction;
    }
}

// Generic media-query listener: reports whether the query matches now (return value) and every later change to the
// given [JSInvokable] method. One registration per id; registering again replaces it. Used by DwsDateRangePicker to
// show one month at phone width, also after a rotation while the calendar is open.
const dwsMediaQueryRegistrations: Record<string, { mediaQuery: MediaQueryList; handler: (e: MediaQueryListEvent) => void }> = {};

function DwsMediaQueryRegister(id: string, dotNetRef: DotNetObject, query: string, methodName: string): boolean {
    DwsMediaQueryUnregister(id);

    if (!window.matchMedia || typeof query !== 'string') {
        return false;
    }

    let mediaQuery = window.matchMedia(query);
    let handler = function (e) {
        dotNetRef.invokeMethodAsync(methodName, e.matches).catch(function () { });
    };

    if (mediaQuery.addEventListener) {
        mediaQuery.addEventListener('change', handler);
    } else {
        mediaQuery.addListener(handler);
    }

    dwsMediaQueryRegistrations[id] = { mediaQuery: mediaQuery, handler: handler };

    return mediaQuery.matches;
}

function DwsMediaQueryUnregister(id: string): void {
    let registration = dwsMediaQueryRegistrations[id];

    if (!registration) {
        return;
    }

    if (registration.mediaQuery.removeEventListener) {
        registration.mediaQuery.removeEventListener('change', registration.handler);
    } else {
        registration.mediaQuery.removeListener(registration.handler);
    }

    delete dwsMediaQueryRegistrations[id];
}

publish("AddClass", AddClass);
publish("RemoveClass", RemoveClass);
publish("GetElementBoundingRect", GetElementBoundingRect);
publish("getVirtualScrollTop", getVirtualScrollTop);
publish("getVirtualClientHeight", getVirtualClientHeight);
publish("getVirtualScrollHeight", getVirtualScrollHeight);
publish("addMouseMoveListener", addMouseMoveListener);
publish("addMouseUpListener", addMouseUpListener);
publish("removeMouseMoveListener", removeMouseMoveListener);
publish("removeMouseUpListener", removeMouseUpListener);
publish("DwsPrefersReducedMotion", DwsPrefersReducedMotion);
publish("DwsGetElementWidth", DwsGetElementWidth);
publish("DwsSetDocumentLanguage", DwsSetDocumentLanguage);
publish("DwsMediaQueryRegister", DwsMediaQueryRegister);
publish("DwsMediaQueryUnregister", DwsMediaQueryUnregister);
